import { Box, Button, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import type { ElementType, ReactNode } from 'react';
import { TbChevronRight } from 'react-icons/tb';
import { SettingsBreadcrumbActions } from './SettingsBreadcrumbBar';
import SettingsTableTabs from './SettingsTableTabs';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_HOVER,
    SETTINGS_BORDER_COLOR,
    SETTINGS_CONTENT_MAX_WIDTH,
} from './constants';

export interface SettingsFlowStep {
    id: string;
    label: string;
    /**
     * Badge after the label, for a step whose work is a running total the
     * earlier steps feed (how many parts are queued up to configure).
     */
    count?: number;
    /** A step the user has not unlocked yet: shown greyed and not clickable. */
    isDisabled?: boolean;
}

export interface SettingsFlowProps {
    steps: SettingsFlowStep[];
    activeStepId: string;
    /** Lets the user jump back to an earlier step by clicking it. */
    onStepSelect?: (id: string) => void;
    onCancel: () => void;
    /** Rendered as "Back" left of Cancel; omit on the first step. */
    onBack?: () => void;
    /** Advances a step. Hidden on the last step, where Save takes over. */
    onNext?: () => void;
    nextLabel?: string;
    isNextDisabled?: boolean;
    /** Commits the flow. Only rendered once the user is on the last step. */
    onSave?: () => void;
    saveLabel?: string;
    isSaveDisabled?: boolean;
    isSaving?: boolean;
    /**
     * Replaces Next and Save while a sub-view of the current step is open (a
     * matrix opened from one row, say), so the primary button leaves that view
     * instead of advancing or committing the whole flow.
     */
    primaryAction?: { label: string; onClick: () => void; isDisabled?: boolean };
    /** Extra buttons, placed left of Cancel (an upload or template action). */
    actions?: ReactNode;
    children: ReactNode;
}

/**
 * A multi-step process that takes over the settings content area: the steps
 * across the top, one screen at a time below, and Cancel/Back/Next/Save on the
 * breadcrumb row. Replaces the stacked modals these flows used to be, so each
 * step gets the whole window and the trail still says where the user came from.
 *
 * The breadcrumb bar itself belongs to Settings.tsx — the flow portals its
 * buttons onto it (SettingsBreadcrumbActions) and adds its own crumbs through
 * useSettingsDetailCrumbs, so a flow never draws a second header. Steps reuse
 * SettingsTableTabs, numbered, rather than introducing a second tab treatment.
 *
 * Children are rendered into a clipped flex column that owns the remaining
 * height, so a step can be a full-height table (SettingsTable) or a scrolling
 * form (SettingsFlowForm) without the flow taking a position on which.
 */
const SettingsFlow = ({
    steps,
    activeStepId,
    onStepSelect,
    onCancel,
    onBack,
    onNext,
    nextLabel = 'Next',
    isNextDisabled = false,
    onSave,
    saveLabel = 'Save',
    isSaveDisabled = false,
    isSaving = false,
    primaryAction,
    actions,
    children,
}: SettingsFlowProps) => {
    const isLastStep = steps.length > 0 && activeStepId === steps[steps.length - 1].id;

    const primaryButtonProps = {
        size: 'sm' as const,
        bg: PARTBAY_BLUE,
        color: 'white',
        _hover: { bg: PARTBAY_BLUE_HOVER },
        _active: { bg: PARTBAY_BLUE_HOVER },
    };

    return (
        <Flex direction="column" h="100%" minH={0} w="100%" overflow="hidden">
            <SettingsBreadcrumbActions>
                {actions}
                <Button size="sm" variant="outlineGray" onClick={onCancel} isDisabled={isSaving}>
                    Cancel
                </Button>
                {onBack && (
                    <Button size="sm" variant="outlineGray" onClick={onBack} isDisabled={isSaving}>
                        Back
                    </Button>
                )}
                {/* Save replaces Next on the final step, and a sub-view's own
                    action replaces both, so only one primary action is ever
                    offered. */}
                {primaryAction && (
                    <Button
                        {...primaryButtonProps}
                        onClick={primaryAction.onClick}
                        isDisabled={primaryAction.isDisabled}
                    >
                        {primaryAction.label}
                    </Button>
                )}
                {!primaryAction && !isLastStep && onNext && (
                    <Button {...primaryButtonProps} onClick={onNext} isDisabled={isNextDisabled}>
                        {nextLabel}
                    </Button>
                )}
                {!primaryAction && isLastStep && onSave && (
                    <Button
                        {...primaryButtonProps}
                        onClick={onSave}
                        isDisabled={isSaveDisabled}
                        isLoading={isSaving}
                        loadingText="Saving"
                    >
                        {saveLabel}
                    </Button>
                )}
            </SettingsBreadcrumbActions>

            {steps.length > 1 && (
                <SettingsTableTabs
                    tabs={steps.map((step, index) => ({
                        id: step.id,
                        label: step.label,
                        number: index + 1,
                        count: step.count,
                        isDisabled: step.isDisabled,
                    }))}
                    value={activeStepId}
                    onChange={(id) => onStepSelect?.(id)}
                    px={4}
                    pt={4}
                    mb={0}
                />
            )}

            <Flex direction="column" flex={1} minH={0} w="100%" overflow="hidden">
                {children}
            </Flex>
        </Flex>
    );
};

export interface SettingsFlowOption {
    id: string;
    title: string;
    description: string;
    icon: ElementType;
    isDisabled?: boolean;
}

export interface SettingsFlowOptionsProps {
    /** Question above the cards, e.g. "How do you want to add users?". */
    title: string;
    options: SettingsFlowOption[];
    onSelect: (id: string) => void;
    /** Marks the card the flow will resume with, when one has been chosen. */
    selectedId?: string;
}

/**
 * The opening step of most flows: a short stack of full-width cards, one per
 * way of doing the task. Clicking a card is the choice — no separate Next —
 * though the flow's Next button still works once a card is selected.
 */
export const SettingsFlowOptions = ({
    title,
    options,
    onSelect,
    selectedId,
}: SettingsFlowOptionsProps) => (
    <Box flex={1} minH={0} overflowY="auto" px={8} py={10}>
        <Box maxW="640px" mx="auto">
            <Text fontSize="xl" fontWeight="semibold" color="gray.800" mb={5}>
                {title}
            </Text>
            <Flex direction="column" gap={3}>
                {options.map((option) => {
                    const isSelected = option.id === selectedId;
                    return (
                        <Flex
                            key={option.id}
                            as="button"
                            type="button"
                            disabled={option.isDisabled}
                            onClick={() => !option.isDisabled && onSelect(option.id)}
                            align="center"
                            justify="space-between"
                            gap={4}
                            w="100%"
                            textAlign="left"
                            px={5}
                            py={4}
                            borderWidth="1px"
                            borderRadius="lg"
                            borderColor={isSelected ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                            bg="white"
                            opacity={option.isDisabled ? 0.5 : 1}
                            cursor={option.isDisabled ? 'not-allowed' : 'pointer'}
                            transition="border-color 0.15s ease, background 0.15s ease"
                            _hover={
                                option.isDisabled
                                    ? undefined
                                    : { borderColor: PARTBAY_BLUE, bg: 'gray.50' }
                            }
                        >
                            <HStack spacing={4} minW={0}>
                                <Flex
                                    boxSize="36px"
                                    borderRadius="md"
                                    bg="gray.100"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                >
                                    <Icon as={option.icon} boxSize={4} color="gray.600" />
                                </Flex>
                                <Box minW={0}>
                                    <Text fontSize="md" fontWeight="semibold" color="gray.800">
                                        {option.title}
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        {option.description}
                                    </Text>
                                </Box>
                            </HStack>
                            <Icon
                                as={TbChevronRight as React.ElementType}
                                boxSize={4}
                                color="gray.400"
                                flexShrink={0}
                            />
                        </Flex>
                    );
                })}
            </Flex>
        </Box>
    </Box>
);

export interface SettingsFlowSplitProps {
    /** Heading above the left pane's content. */
    leftTitle: string;
    left: ReactNode;
    /** Heading above the right pane; omit for an unlabelled panel. */
    rightTitle?: string;
    /** One line under the right pane's heading. */
    rightDescription?: string;
    /** Right-aligned controls on the right pane's heading row. */
    rightActions?: ReactNode;
    right: ReactNode;
    /**
     * Drop the right pane's padding and its own scrollbar, for a pane whose
     * content manages height itself — a SettingsTable, whose sticky header
     * stays put while only its rows scroll.
     */
    isRightFlush?: boolean;
    /**
     * The same treatment for the left pane: no padding and no scrollbar of its
     * own, for content that manages its own height — a form above a table that
     * should take the leftover space and scroll on its own.
     */
    isLeftFlush?: boolean;
    /** Fraction of the width given to the left pane. */
    leftWidth?: string;
}

/**
 * Two panes side by side for one step: what you are editing on the left, and
 * the detail it expands into on the right (a permissions matrix, a preview of
 * what will be created). The right pane is tinted so the split reads as
 * primary / secondary rather than as two equal forms, and each pane scrolls on
 * its own so a long list never pushes the other out of view.
 */
export const SettingsFlowSplit = ({
    leftTitle,
    left,
    rightTitle,
    rightDescription,
    rightActions,
    right,
    isRightFlush = false,
    isLeftFlush = false,
    leftWidth = '50%',
}: SettingsFlowSplitProps) => (
    <Flex flex={1} minH={0} w="100%" overflow="hidden">
        <Flex
            direction="column"
            w={leftWidth}
            minW={0}
            minH={0}
            borderRightWidth="1px"
            borderColor={SETTINGS_BORDER_COLOR}
        >
            <Box px={6} pt={6} pb={4} flexShrink={0}>
                <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    {leftTitle}
                </Text>
            </Box>
            {isLeftFlush ? (
                <Flex direction="column" flex={1} minH={0} w="100%" overflow="hidden">
                    {left}
                </Flex>
            ) : (
                <Box flex={1} minH={0} overflowY="auto" px={6} pb={8}>
                    {left}
                </Box>
            )}
        </Flex>
        <Flex direction="column" flex={1} minW={0} minH={0} bg={isRightFlush ? 'white' : 'gray.50'}>
            {(rightTitle || rightActions) && (
                <Flex
                    align="flex-start"
                    justify="space-between"
                    gap={4}
                    px={6}
                    pt={6}
                    pb={4}
                    flexShrink={0}
                >
                    <Box minW={0}>
                        <Text fontSize="md" fontWeight="semibold" color="gray.800">
                            {rightTitle}
                        </Text>
                        {rightDescription && (
                            <Text fontSize="sm" color="gray.500" mt={1}>
                                {rightDescription}
                            </Text>
                        )}
                    </Box>
                    {rightActions && (
                        <HStack spacing={2} flexShrink={0}>
                            {rightActions}
                        </HStack>
                    )}
                </Flex>
            )}
            {isRightFlush ? (
                <Flex direction="column" flex={1} minH={0} w="100%" overflow="hidden">
                    {right}
                </Flex>
            ) : (
                <Box flex={1} minH={0} overflowY="auto" px={6} pb={8}>
                    {right}
                </Box>
            )}
        </Flex>
    </Flex>
);

export interface SettingsFlowFormProps {
    children: ReactNode;
    /** Width of the centered column; defaults to the settings content width. */
    maxW?: string;
}

/** Scrolling, centered column for a form-shaped step. */
export const SettingsFlowForm = ({
    children,
    maxW = SETTINGS_CONTENT_MAX_WIDTH,
}: SettingsFlowFormProps) => (
    <Box flex={1} minH={0} overflowY="auto" px={8} pt={6} pb={10}>
        <Box maxW={maxW} mx="auto">
            {children}
        </Box>
    </Box>
);

export default SettingsFlow;
